/**
 * Factor Interaction Matrix - Critical Mathematical Foundation
 * 
 * Models non-linear factor interactions and correlation clustering during stress periods.
 * Essential for capturing realistic portfolio behavior during market stress.
 * 
 * Key Concepts:
 * - Linear factors (interest rates, inflation) affect returns independently
 * - Non-linear interactions (e.g., high rates + high inflation = amplified impact)
 * - Correlation clustering (factors move together during stress)
 */

export interface FactorSet {
  interestRate: number;
  inflation: number;
  creditSpread: number;
  volatility: number;
  liquidity: number;
  currency: number;
}

export interface FactorImpact {
  growthImpact: number;
  defensiveImpact: number;
  correlationImpact: number;
  volatilityImpact: number;
}

export interface InteractionEffect {
  magnitude: number;
  description: string;
  affectedAssets: ('growth' | 'defensive' | 'both')[];
}

export class FactorInteractionMatrix {
  // Interaction coefficients (calibrated from historical data)
  private readonly interactionCoefficients = {
    // Rate-Inflation interaction (stagflation scenario)
    rateInflation: {
      growth: -0.15,      // High rates + high inflation severely hurt equities
      defensive: -0.08,   // Bonds also suffer but less
      correlation: 0.25   // Correlations increase
    },
    
    // Rate-Credit interaction (tightening + stress)
    rateCredit: {
      growth: -0.12,
      defensive: -0.05,
      correlation: 0.20
    },
    
    // Volatility-Liquidity interaction (stress amplification)
    volatilityLiquidity: {
      growth: -0.20,      // Severe impact on growth assets
      defensive: -0.08,
      correlation: 0.35   // Strong correlation clustering
    },
    
    // Credit-Volatility interaction (risk-off cascade)
    creditVolatility: {
      growth: -0.18,
      defensive: -0.06,
      correlation: 0.30
    },
    
    // Inflation-Currency interaction (FX pass-through)
    inflationCurrency: {
      growth: -0.08,
      defensive: -0.04,
      correlation: 0.10
    },
    
    // Triple interaction: Rate + Inflation + Credit (perfect storm)
    rateInflationCredit: {
      growth: -0.25,      // Maximum stress scenario
      defensive: -0.12,
      correlation: 0.45   // Extreme correlation clustering
    }
  };

  /**
   * Calculate comprehensive factor impacts including interactions
   */
  calculateFactorImpacts(factors: FactorSet): FactorImpact {
    // Start with zero impact
    let growthImpact = 0;
    let defensiveImpact = 0;
    let correlationImpact = 0;
    let volatilityImpact = 1.0; // Multiplicative

    // 1. Linear factor impacts
    const linearImpacts = this.calculateLinearImpacts(factors);
    growthImpact += linearImpacts.growthImpact;
    defensiveImpact += linearImpacts.defensiveImpact;
    correlationImpact += linearImpacts.correlationImpact;
    volatilityImpact *= linearImpacts.volatilityImpact;

    // 2. Pairwise interactions
    const pairwiseInteractions = this.calculatePairwiseInteractions(factors);
    growthImpact += pairwiseInteractions.growthImpact;
    defensiveImpact += pairwiseInteractions.defensiveImpact;
    correlationImpact += pairwiseInteractions.correlationImpact;
    volatilityImpact *= pairwiseInteractions.volatilityImpact;

    // 3. Higher-order interactions (triple and beyond)
    const higherOrderInteractions = this.calculateHigherOrderInteractions(factors);
    growthImpact += higherOrderInteractions.growthImpact;
    defensiveImpact += higherOrderInteractions.defensiveImpact;
    correlationImpact += higherOrderInteractions.correlationImpact;
    volatilityImpact *= higherOrderInteractions.volatilityImpact;

    return {
      growthImpact,
      defensiveImpact,
      correlationImpact: Math.max(-0.3, Math.min(0.6, correlationImpact)), // Clamp
      volatilityImpact: Math.max(0.5, Math.min(3.0, volatilityImpact)) // Clamp
    };
  }

  /**
   * Calculate linear factor impacts (first-order effects)
   */
  private calculateLinearImpacts(factors: FactorSet): FactorImpact {
    let growthImpact = 0;
    let defensiveImpact = 0;
    let correlationImpact = 0;
    let volatilityImpact = 1.0;

    // Interest rate impact
    if (Math.abs(factors.interestRate) > 0.001) {
      growthImpact += factors.interestRate * -0.30;  // Negative for equities
      defensiveImpact += factors.interestRate * -0.80; // Very negative for bonds
      correlationImpact += Math.abs(factors.interestRate) * 0.15;
    }

    // Inflation impact
    if (Math.abs(factors.inflation) > 0.001) {
      const inflationShock = factors.inflation - 0.025; // Relative to 2.5% target
      growthImpact += inflationShock * -0.20;
      defensiveImpact += inflationShock * -0.60;
      if (inflationShock > 0) {
        correlationImpact += inflationShock * 0.12;
      }
    }

    // Credit spread impact
    if (Math.abs(factors.creditSpread) > 0.001) {
      growthImpact += factors.creditSpread * -1.2; // High sensitivity
      defensiveImpact += factors.creditSpread * -0.3;
      correlationImpact += Math.abs(factors.creditSpread) * 0.20;
    }

    // Volatility impact
    if (factors.volatility > 0.1) {
      volatilityImpact *= (1 + factors.volatility);
      correlationImpact += factors.volatility * 0.25;
    }

    // Liquidity impact (binary: present or absent)
    if (factors.liquidity > 0.5) {
      growthImpact += -0.02; // -2% shock
      defensiveImpact += -0.01;
      correlationImpact += 0.15;
      volatilityImpact *= 1.3;
    }

    // Currency impact
    if (Math.abs(factors.currency) > 0.001) {
      growthImpact += factors.currency * 0.10; // Some exposure
      defensiveImpact += factors.currency * 0.05;
    }

    return {
      growthImpact,
      defensiveImpact,
      correlationImpact,
      volatilityImpact
    };
  }

  /**
   * Calculate pairwise factor interactions (second-order effects)
   */
  private calculatePairwiseInteractions(factors: FactorSet): FactorImpact {
    let growthImpact = 0;
    let defensiveImpact = 0;
    let correlationImpact = 0;
    let volatilityImpact = 1.0;

    // Rate-Inflation interaction (stagflation)
    if (factors.interestRate > 0.005 && factors.inflation > 0.03) {
      const interactionStrength = factors.interestRate * (factors.inflation - 0.025);
      growthImpact += interactionStrength * this.interactionCoefficients.rateInflation.growth;
      defensiveImpact += interactionStrength * this.interactionCoefficients.rateInflation.defensive;
      correlationImpact += interactionStrength * this.interactionCoefficients.rateInflation.correlation;
    }

    // Rate-Credit interaction
    if (factors.interestRate > 0.003 && factors.creditSpread > 0.003) {
      const interactionStrength = factors.interestRate * factors.creditSpread;
      growthImpact += interactionStrength * this.interactionCoefficients.rateCredit.growth;
      defensiveImpact += interactionStrength * this.interactionCoefficients.rateCredit.defensive;
      correlationImpact += interactionStrength * this.interactionCoefficients.rateCredit.correlation;
    }

    // Volatility-Liquidity interaction (stress amplification)
    if (factors.volatility > 0.2 && factors.liquidity > 0.5) {
      const interactionStrength = factors.volatility * factors.liquidity;
      growthImpact += interactionStrength * this.interactionCoefficients.volatilityLiquidity.growth;
      defensiveImpact += interactionStrength * this.interactionCoefficients.volatilityLiquidity.defensive;
      correlationImpact += interactionStrength * this.interactionCoefficients.volatilityLiquidity.correlation;
      volatilityImpact *= (1 + interactionStrength * 0.5);
    }

    // Credit-Volatility interaction
    if (factors.creditSpread > 0.004 && factors.volatility > 0.15) {
      const interactionStrength = factors.creditSpread * factors.volatility * 20; // Scale up
      growthImpact += interactionStrength * this.interactionCoefficients.creditVolatility.growth;
      defensiveImpact += interactionStrength * this.interactionCoefficients.creditVolatility.defensive;
      correlationImpact += interactionStrength * this.interactionCoefficients.creditVolatility.correlation;
    }

    // Inflation-Currency interaction
    if (Math.abs(factors.inflation - 0.025) > 0.01 && Math.abs(factors.currency) > 0.01) {
      const interactionStrength = (factors.inflation - 0.025) * factors.currency;
      growthImpact += interactionStrength * this.interactionCoefficients.inflationCurrency.growth;
      defensiveImpact += interactionStrength * this.interactionCoefficients.inflationCurrency.defensive;
      correlationImpact += Math.abs(interactionStrength) * this.interactionCoefficients.inflationCurrency.correlation;
    }

    return {
      growthImpact,
      defensiveImpact,
      correlationImpact,
      volatilityImpact
    };
  }

  /**
   * Calculate higher-order interactions (triple and beyond)
   */
  private calculateHigherOrderInteractions(factors: FactorSet): FactorImpact {
    let growthImpact = 0;
    let defensiveImpact = 0;
    let correlationImpact = 0;
    let volatilityImpact = 1.0;

    // Triple interaction: Rate + Inflation + Credit (perfect storm)
    if (
      factors.interestRate > 0.005 &&
      factors.inflation > 0.03 &&
      factors.creditSpread > 0.004
    ) {
      const interactionStrength = 
        factors.interestRate * 
        (factors.inflation - 0.025) * 
        factors.creditSpread * 
        100; // Scale up for visibility
      
      growthImpact += interactionStrength * this.interactionCoefficients.rateInflationCredit.growth;
      defensiveImpact += interactionStrength * this.interactionCoefficients.rateInflationCredit.defensive;
      correlationImpact += interactionStrength * this.interactionCoefficients.rateInflationCredit.correlation;
      volatilityImpact *= (1 + interactionStrength * 0.8);
    }

    // Quadruple interaction: Rate + Inflation + Credit + Volatility (extreme stress)
    if (
      factors.interestRate > 0.007 &&
      factors.inflation > 0.035 &&
      factors.creditSpread > 0.005 &&
      factors.volatility > 0.3
    ) {
      // This is a crisis scenario - amplify all effects
      growthImpact += -0.05; // Additional -5% shock
      defensiveImpact += -0.025; // Additional -2.5% shock
      correlationImpact += 0.20; // Strong clustering
      volatilityImpact *= 1.5;
    }

    return {
      growthImpact,
      defensiveImpact,
      correlationImpact,
      volatilityImpact
    };
  }

  /**
   * Detect and classify interaction effects for reporting
   */
  detectSignificantInteractions(factors: FactorSet): InteractionEffect[] {
    const effects: InteractionEffect[] = [];

    // Rate-Inflation (Stagflation)
    if (factors.interestRate > 0.005 && factors.inflation > 0.03) {
      effects.push({
        magnitude: factors.interestRate * (factors.inflation - 0.025) * 50,
        description: 'Stagflation scenario: High rates + High inflation',
        affectedAssets: ['both']
      });
    }

    // Volatility-Liquidity (Stress amplification)
    if (factors.volatility > 0.2 && factors.liquidity > 0.5) {
      effects.push({
        magnitude: factors.volatility * factors.liquidity * 2,
        description: 'Market stress: High volatility + Liquidity crisis',
        affectedAssets: ['growth']
      });
    }

    // Credit-Volatility (Risk-off)
    if (factors.creditSpread > 0.004 && factors.volatility > 0.15) {
      effects.push({
        magnitude: factors.creditSpread * factors.volatility * 40,
        description: 'Risk-off cascade: Credit stress + Volatility spike',
        affectedAssets: ['growth']
      });
    }

    // Perfect storm
    if (
      factors.interestRate > 0.005 &&
      factors.inflation > 0.03 &&
      factors.creditSpread > 0.004
    ) {
      effects.push({
        magnitude: factors.interestRate * (factors.inflation - 0.025) * factors.creditSpread * 200,
        description: 'Perfect storm: Rates + Inflation + Credit stress',
        affectedAssets: ['both']
      });
    }

    return effects.filter(e => e.magnitude > 0.001); // Only significant effects
  }

  /**
   * Get correlation clustering coefficient for current factor state
   * Returns how much correlations increase due to factor clustering
   */
  getCorrelationClusteringCoefficient(factors: FactorSet): number {
    // Count how many factors are in stress territory
    let stressFactorCount = 0;
    
    if (factors.interestRate > 0.005) stressFactorCount++;
    if (factors.inflation > 0.03) stressFactorCount++;
    if (factors.creditSpread > 0.004) stressFactorCount++;
    if (factors.volatility > 0.2) stressFactorCount++;
    if (factors.liquidity > 0.5) stressFactorCount++;

    // Clustering increases exponentially with number of stress factors
    // 0 factors: 0% increase
    // 1 factor: 5% increase
    // 2 factors: 15% increase
    // 3+ factors: 30%+ increase
    const clusteringMap = [0, 0.05, 0.15, 0.30, 0.40, 0.50];
    
    return clusteringMap[Math.min(stressFactorCount, 5)];
  }

  /**
   * Calculate sequence risk amplification during factor stress
   * Returns multiplier for sequence risk impact
   */
  getSequenceRiskAmplification(factors: FactorSet, yearsFromRetirement: number): number {
    // Only amplify if we're in the critical sequence risk window (-5 to +10 years)
    if (Math.abs(yearsFromRetirement) > 5) {
      return 1.0; // No amplification outside critical window
    }

    // Calculate factor stress intensity
    const stressIntensity = 
      Math.max(0, (factors.interestRate - 0.003) * 200) +
      Math.max(0, (factors.inflation - 0.03) * 30) +
      Math.max(0, (factors.creditSpread - 0.003) * 200) +
      Math.max(0, factors.volatility * 2) +
      (factors.liquidity > 0.5 ? 0.3 : 0);

    // Distance from retirement (0 at retirement, 1 at ±5 years)
    const retirementProximity = 1 - Math.abs(yearsFromRetirement) / 5;

    // Amplification increases near retirement when factors are stressed
    const amplification = 1 + stressIntensity * retirementProximity * 0.5;

    // Clamp to reasonable range [1.0, 2.5]
    return Math.max(1.0, Math.min(2.5, amplification));
  }
}
